import { fetchAuthSession } from 'aws-amplify/auth'
import { EducationalAreaValue } from './constants'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

// ── Types (mirror OpenAPI schemas) ──────────────────────────────────────────

export interface UserProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  familyId: string | null
  createdAt: string
}

export interface ApiFamily {
  id: string
  name: string
  createdAt: string
}

export type MemberRole = 'owner' | 'contributor' | 'viewer'

export interface FamilyMember {
  userId: string
  familyId: string
  firstName: string
  lastName: string
  role: MemberRole
  joinedAt: string
}

export interface ApiChild {
  id: string
  familyId: string
  firstName: string
  dateOfBirth: string | null
  createdAt: string
}

export interface Book {
  id: string
  childId: string
  familyId: string
  loggedBy: string
  title: string
  author: string | null
  dateRead: string | null
  notes: string | null
  createdAt: string
  mediaAttachments: string[]
  reviewStatus?: 'pending' | 'accepted' | null
  reviewedBy?: string | null
  reviewedAt?: string | null
  educationalAreas?: string[] | null
  areaConfidence?: number | null
  areaClassificationSource?: string | null
  isFeatured: boolean
}

export interface BookBody {
  title: string
  author?: string | null
  dateRead?: string | null
  notes?: string | null
  educationalAreas?: string[] | null
  isFeatured?: boolean | null
}

export interface Activity {
  id: string
  childId: string
  familyId: string
  loggedBy: string
  date: string | null
  description: string
  educationalAreas: string[]
  descriptionSource: 'manual' | 'ai_generated' | null
  notes: string | null
  areaConfidence: number | null
  areaClassificationSource: 'manual' | 'auto' | 'suggested_accepted' | 'suggested_overridden' | null
  reviewStatus: 'pending' | 'accepted' | null
  reviewedBy: string | null
  reviewedAt: string | null
  mediaAttachments: string[]
  createdAt: string
  isFeatured: boolean
}

export type PendingItem =
  | (Book & { itemType: 'book' })
  | (Activity & { itemType: 'activity' })

export interface ActivityBody {
  description: string
  educationalAreas: string[]
  date?: string | null
  notes?: string | null
  isFeatured?: boolean | null
}

export interface ActivityUploadUrl {
  key: string
  url: string
}

export interface ActivityUploadUrlsResponse {
  uploadUrls: ActivityUploadUrl[]
}

export interface ActivityFromImagesBody {
  s3Keys: string[]
  notes?: string | null
  date?: string | null
}

export interface ChildWeeklyStats {
  activitiesThisWeek: number
  booksThisWeek: number
  topicsCoveredThisWeek: number
}

export interface PortfolioChild {
  id: string
  firstName: string
  age: { years: number; months: number } | null
}

export interface PortfolioDateRange {
  startDate: string
  endDate: string
}

export interface PortfolioSummary {
  overall: string
  byArea: Record<string, string>
}

export interface PortfolioResponse {
  child: PortfolioChild
  dateRange: PortfolioDateRange
  activitiesByArea: Record<string, Activity[]>
  books: Book[]
  summary: PortfolioSummary | null
  generatedAt: string | null
}

// ── Internal fetch helper ────────────────────────────────────────────────────

async function getToken(): Promise<string> {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString()
  if (!token) throw new Error('Not authenticated')
  return token
}

async function apiFetchOrNull<T>(path: string, init: RequestInit = {}): Promise<T | null> {
  const token = await getToken()
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  })
  if (res.status === 404) return null
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken()
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ── API functions ────────────────────────────────────────────────────────────

export const api = {
  me: {
    get: () => apiFetch<UserProfile>('/me'),
    create: (body: { firstName: string; lastName: string }) =>
      apiFetch<UserProfile>('/me', { method: 'POST', body: JSON.stringify(body) }),
    update: (body: { firstName: string; lastName: string }) =>
      apiFetch<UserProfile>('/me', { method: 'PUT', body: JSON.stringify(body) }),
  },

  families: {
    create: (body: { name: string }) =>
      apiFetch<ApiFamily>('/families', { method: 'POST', body: JSON.stringify(body) }),
    get: (familyId: string) =>
      apiFetch<ApiFamily>(`/families/${familyId}`),
    update: (familyId: string, body: { name: string }) =>
      apiFetch<ApiFamily>(`/families/${familyId}`, { method: 'PUT', body: JSON.stringify(body) }),
    pending: (familyId: string) =>
      apiFetch<PendingItem[]>(`/families/${familyId}/pending`),
  },

  members: {
    list: (familyId: string) =>
      apiFetch<FamilyMember[]>(`/families/${familyId}/members`),
    invite: (
      familyId: string,
      body: { email: string; firstName: string; lastName: string; role: MemberRole },
    ) =>
      apiFetch<FamilyMember>(`/families/${familyId}/members`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateRole: (familyId: string, userId: string, body: { role: MemberRole }) =>
      apiFetch<FamilyMember>(`/families/${familyId}/members/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    remove: (familyId: string, userId: string) =>
      apiFetch<void>(`/families/${familyId}/members/${userId}`, { method: 'DELETE' }),
  },

  children: {
    list: (familyId: string) =>
      apiFetch<ApiChild[]>(`/families/${familyId}/children`),
    create: (familyId: string, body: { firstName: string; dateOfBirth?: string | null }) =>
      apiFetch<ApiChild>(`/families/${familyId}/children`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (
      familyId: string,
      childId: string,
      body: { firstName: string; dateOfBirth?: string | null },
    ) =>
      apiFetch<ApiChild>(`/families/${familyId}/children/${childId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    delete: (familyId: string, childId: string) =>
      apiFetch<void>(`/families/${familyId}/children/${childId}`, { method: 'DELETE' }),
    stats: (familyId: string, childId: string) =>
      apiFetch<ChildWeeklyStats>(`/families/${familyId}/children/${childId}/stats`),
  },

  books: {
    list: (familyId: string, childId: string, educationalArea?: EducationalAreaValue | null) => {
      const params = new URLSearchParams()
      if (educationalArea) params.set('educational_area', educationalArea)
      const qs = params.toString()
      return apiFetch<Book[]>(`/families/${familyId}/children/${childId}/books${qs ? `?${qs}` : ''}`)
    },
    create: (familyId: string, childId: string, body: BookBody) =>
      apiFetch<Book>(`/families/${familyId}/children/${childId}/books`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    createFromImage: async (familyId: string, childId: string, image: File, date?: string | null): Promise<Book> => {
      const token = await getToken()
      const form = new FormData()
      form.append('image', image)
      if (date) form.append('date', date)
      const res = await fetch(`${BASE_URL}/families/${familyId}/children/${childId}/books/from-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const err = (body as { error?: unknown }).error
        const msg = Array.isArray(err)
          ? (err[0] as { message?: string })?.message ?? `HTTP ${res.status}`
          : typeof err === 'string'
          ? err
          : `HTTP ${res.status}`
        throw new Error(msg)
      }
      return res.json() as Promise<Book>
    },
    update: (familyId: string, childId: string, bookId: string, body: BookBody) =>
      apiFetch<Book>(`/families/${familyId}/children/${childId}/books/${bookId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    delete: (familyId: string, childId: string, bookId: string) =>
      apiFetch<void>(`/families/${familyId}/children/${childId}/books/${bookId}`, {
        method: 'DELETE',
      }),
  },

  activities: {
    list: (familyId: string, childId: string, educationalArea?: EducationalAreaValue | null) => {
      const params = new URLSearchParams()
      if (educationalArea) params.set('educational_area', educationalArea)
      const qs = params.toString()
      return apiFetch<Activity[]>(`/families/${familyId}/children/${childId}/activities${qs ? `?${qs}` : ''}`)
    },
    create: (familyId: string, childId: string, body: ActivityBody) =>
      apiFetch<Activity>(`/families/${familyId}/children/${childId}/activities`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    createFromImage: async (familyId: string, childId: string, image: File): Promise<Activity> => {
      const token = await getToken()
      const form = new FormData()
      form.append('image', image)
      const res = await fetch(`${BASE_URL}/families/${familyId}/children/${childId}/activities/from-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const err = (body as { error?: unknown }).error
        const msg = Array.isArray(err)
          ? (err[0] as { message?: string })?.message ?? `HTTP ${res.status}`
          : typeof err === 'string'
          ? err
          : `HTTP ${res.status}`
        throw new Error(msg)
      }
      return res.json() as Promise<Activity>
    },
    getUploadUrls: (familyId: string, childId: string, filenames: string[]) =>
      apiFetch<ActivityUploadUrlsResponse>(
        `/families/${familyId}/children/${childId}/activities/upload-urls`,
        { method: 'POST', body: JSON.stringify({ filenames }) },
      ),
    createFromImages: (familyId: string, childId: string, body: ActivityFromImagesBody) =>
      apiFetch<Activity>(
        `/families/${familyId}/children/${childId}/activities/from-images`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    update: (familyId: string, childId: string, activityId: string, body: ActivityBody) =>
      apiFetch<Activity>(`/families/${familyId}/children/${childId}/activities/${activityId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    delete: (familyId: string, childId: string, activityId: string) =>
      apiFetch<void>(`/families/${familyId}/children/${childId}/activities/${activityId}`, {
        method: 'DELETE',
      }),
  },

  portfolio: {
    get: (familyId: string, childId: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const qs = params.toString()
      return apiFetchOrNull<PortfolioResponse>(
        `/families/${familyId}/children/${childId}/portfolio${qs ? `?${qs}` : ''}`,
      )
    },
    generate: (familyId: string, childId: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const qs = params.toString()
      return apiFetch<PortfolioResponse>(
        `/families/${familyId}/children/${childId}/portfolio${qs ? `?${qs}` : ''}`,
        { method: 'POST' },
      )
    },
  },
}
